import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGlobalStore } from '../../stores/globalStore';
import { isPsiEmail } from '../../utils/psiStaff';
import { GRANNY_EVENT } from './grannyTrigger';
import './GrannyEasterEgg.css';

export const GRANNY_SHOW_MS = 5000;
export const GRANNY_MESSAGE = 'Ooh, much easier on these old eyes!';
export const GRANNY_ALT = 'Cartoon grandmother with glasses';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/**
 * The cartoon grandmother PSI staff see after choosing the X-Large size (see
 * grannyTrigger.ts). PSI staff only, by the psibuffalo.com login itself:
 * not useIsPsiStaff, which also lets in every login on a dev build.
 */
export default function GrannyEasterEgg() {
  const isPsi = isPsiEmail(useGlobalStore((s) => s.userEmail));
  // A counter rather than a flag, so choosing X-Large again restarts the timer.
  const [shownAt, setShownAt] = useState(0);
  const [animate, setAnimate] = useState(true);
  const dismiss = useCallback(() => setShownAt(0), []);

  useEffect(() => {
    if (!isPsi) return undefined;
    const show = () => {
      setAnimate(!prefersReducedMotion());
      setShownAt((n) => n + 1);
    };
    window.addEventListener(GRANNY_EVENT, show);
    return () => window.removeEventListener(GRANNY_EVENT, show);
  }, [isPsi]);

  useEffect(() => {
    if (!shownAt) return undefined;
    const timer = window.setTimeout(dismiss, GRANNY_SHOW_MS);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [shownAt, dismiss]);

  if (!isPsi || !shownAt) return null;

  // Portaled to <body>, above MUI's modal layer: the avatar menu's size picker is
  // a Popover that stays open over this corner and aria-hides the app beneath it.
  return createPortal(
    <div role="status" className={`granny-egg${animate ? ' granny-egg--animate' : ''}`}>
      <button type="button" className="granny-egg__card" onClick={dismiss}>
        <GrannySvg />
        <span className="granny-egg__text">{GRANNY_MESSAGE}</span>
      </button>
    </div>,
    document.body,
  );
}

function GrannySvg() {
  // Colours live in GrannyEasterEgg.css (gr-* classes), not inline hex.
  return (
    <svg className="granny-egg__art" viewBox="0 0 120 120" width="108" height="108" role="img" aria-label={GRANNY_ALT}>
      <title>{GRANNY_ALT}</title>
      <circle cx="60" cy="60" r="58" className="granny-egg__halo" />
      {/* cardigan, blouse, buttons and pearls */}
      <path className="gr-cardigan" d="M20 120 C22 94 38 82 60 82 C82 82 98 94 100 120 Z" />
      <path className="gr-blouse" d="M49 83 L60 106 L71 83 Z" />
      <path className="gr-seam" d="M60 106 L60 120" />
      <circle className="gr-blouse" cx="56" cy="111" r="1.8" />
      <circle className="gr-blouse" cx="56" cy="117" r="1.8" />
      <rect className="gr-neck" x="53" y="70" width="14" height="14" rx="4" />
      {[50, 54, 58, 62, 66, 70].map((x, i) => (
        <circle key={x} className="gr-pearl" cx={x} cy={85 + (i === 0 || i === 5 ? 0 : i === 1 || i === 4 ? 1.6 : 2.4)} r="1.9" />
      ))}
      {/* hair, bun and ears */}
      <circle className="gr-hair" cx="60" cy="49" r="27" />
      <circle className="gr-bun" cx="60" cy="19" r="11" />
      <path className="gr-bun-line" d="M53 17 Q60 13 67 17" />
      <ellipse className="gr-ear" cx="38" cy="57" rx="4" ry="5.5" />
      <ellipse className="gr-ear" cx="82" cy="57" rx="4" ry="5.5" />
      {/* face */}
      <ellipse className="gr-face" cx="60" cy="55" rx="21" ry="23" />
      <path className="gr-hair" d="M38 50 C38 31 50 27 60 27 C70 27 82 31 82 50 C76 40 68 37 60 38 C52 37 44 40 38 50 Z" />
      <path className="gr-shine" d="M45 39 Q52 34 58 36" />
      <circle className="gr-cheek" cx="45" cy="64" r="4" />
      <circle className="gr-cheek" cx="75" cy="64" r="4" />
      {/* happy closed eyes behind round glasses */}
      <path className="gr-eye" d="M47.5 55.5 Q51 52 54.5 55.5" />
      <path className="gr-eye" d="M65.5 55.5 Q69 52 72.5 55.5" />
      <circle className="gr-lens" cx="51" cy="55" r="7.5" />
      <circle className="gr-lens" cx="69" cy="55" r="7.5" />
      <path className="gr-frame" d="M58.5 54 Q60 52.5 61.5 54" />
      <path className="gr-frame" d="M43.5 54 L39 52.5 M76.5 54 L81 52.5" />
      <path className="gr-nose" d="M60 58 Q58.5 62 60.5 62.5" />
      <path className="gr-smile" d="M52 67 Q60 74 68 67" />
    </svg>
  );
}
